import type { MentionResource } from '@/types/mention';

export const END_CHARS = [
  ' ',
  '，',
  ',',
  '。',
  '.',
  '？',
  '?',
  '！',
  '!',
  '：',
  ':',
  '；',
  ';',
  '\n',
];

export interface MentionContext {
  startIndex: number;
  keyword: string;
}

export const MENTION_REGEX = /@\[[^\]]+\]\([^)]+\)/g;

/** UUID v4/v7 格式片段（不带分组标记，供拼接复用） */
const UUID_PATTERN = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

/** 匹配紧跟提及 `]` 之后的 `<UUID>` 标记，用于剥离入库/展示文本中的素材 ID */
export const MENTION_RESOURCE_ID_REGEX = new RegExp(`\\]<${UUID_PATTERN}>`, 'g');

/** 严格校验一个字符串是否为标准 UUID（用于避免嵌入无法被剥离的非 UUID 标记） */
const MENTION_RESOURCE_ID_TEST = new RegExp(`^${UUID_PATTERN}$`);

/**
 * 由素材 ID 构造 `<UUID>` 后缀：仅当 ID 为标准 UUID 时才输出，否则返回空串。
 * 校验可避免嵌入非 UUID 标记导致展示/后端无法剥离而泄漏。
 */
export function buildMentionResourceIdSuffixFromId(resourceId?: string | null): string {
  if (!resourceId || !MENTION_RESOURCE_ID_TEST.test(resourceId)) return '';
  return `<${resourceId}>`;
}

/**
 * 为 resource 类提及构造 `<submitResourceId>` 后缀，用于提交入库时精确标识素材。
 * 无素材或 ID 非 UUID 时返回空串。
 */
export function buildMentionResourceIdSuffix(resource?: MentionResource | null): string {
  if (!resource) return '';
  return buildMentionResourceIdSuffixFromId(resource.submitResourceId ?? resource.resourceId);
}

/**
 * 去掉文本中所有紧跟提及 `]` 之后的 `<UUID>` 标记，仅保留 `]`。
 * 供展示、复制、喂入编辑器等场景过滤 UUID；非提及处的 `<...>` 不受影响（严格 UUID 匹配）。
 */
export function stripMentionResourceIds(text: string): string {
  if (!text) return text;
  return text.replace(MENTION_RESOURCE_ID_REGEX, ']');
}

export interface MentionResourceIdMarker {
  /** 不含 UUID 的提及标签，如 `@角色[少女]` */
  display: string;
  /** 该提及携带的素材 UUID */
  resourceId: string;
  /** 在 cleanText 中的起始下标 */
  start: number;
  /** 在 cleanText 中的结束下标 */
  end: number;
}

/**
 * 解析文本中带 `<UUID>` 的提及，返回去 UUID 的干净文本与按出现顺序排列的标记。
 * markers 用于「应用提示词」时按 display 出现顺序绑定正确素材，规避重名。
 */
export function parseMentionResourceIds(text: string): {
  cleanText: string;
  markers: MentionResourceIdMarker[];
} {
  if (!text) return { cleanText: text ?? '', markers: [] };

  const mentionWithId = new RegExp(`(@[^\\s@/[]+\\[[^\\]]+\\])<(${UUID_PATTERN})>`, 'g');
  const markers: MentionResourceIdMarker[] = [];
  let cleanText = '';
  let lastIndex = 0;

  for (const match of text.matchAll(mentionWithId)) {
    const matchStart = match.index ?? 0;
    const display = match[1];
    const resourceId = match[2];
    cleanText += text.slice(lastIndex, matchStart);
    const start = cleanText.length;
    cleanText += display;
    const end = cleanText.length;
    markers.push({ display, resourceId, start, end });
    lastIndex = matchStart + match[0].length;
  }
  cleanText += text.slice(lastIndex);

  return { cleanText, markers };
}

/** 匹配 /相机控制、/角度控制、/灯光控制 等指令标记 */
export const SLASH_COMMAND_REGEX = /\/[^\s，,。.？?！!：:；;\n]+/g;

export interface SlashCommandContext {
  startIndex: number;
  keyword: string;
}

export function detectMentionContext(text: string, cursorPos: number): MentionContext | null {
  const textBeforeCursor = text.slice(0, cursorPos);
  const lastAtIndex = textBeforeCursor.lastIndexOf('@');
  if (lastAtIndex === -1) return null;

  const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);

  // 已经是完整标记或遇到结束符号，忽略
  if (textAfterAt.includes(')')) return null;
  if (END_CHARS.some((char) => textAfterAt.includes(char))) return null;

  return { startIndex: lastAtIndex, keyword: textAfterAt };
}

export function detectSlashCommandContext(
  text: string,
  cursorPos: number
): SlashCommandContext | null {
  const textBeforeCursor = text.slice(0, cursorPos);
  const lastSlashIndex = textBeforeCursor.lastIndexOf('/');
  if (lastSlashIndex === -1) return null;

  // 斜杠前必须是行首或空白字符，避免 URL 中的 / 误触发
  if (lastSlashIndex > 0) {
    const charBefore = textBeforeCursor[lastSlashIndex - 1];
    if (!/[\s\n]/.test(charBefore)) return null;
  }

  const textAfterSlash = textBeforeCursor.slice(lastSlashIndex + 1);
  if (END_CHARS.some((char) => textAfterSlash.includes(char))) return null;

  return { startIndex: lastSlashIndex, keyword: textAfterSlash };
}

export function splitTextWithMentions(
  text: string
): Array<{ type: 'mention' | 'text'; value: string }> {
  if (!text) return [{ type: 'text', value: '' }];

  const regex = new RegExp(MENTION_REGEX);
  const tokens: Array<{ type: 'mention' | 'text'; value: string }> = [];
  let lastIndex = 0;

  for (const match of text.matchAll(regex)) {
    const start = match.index ?? 0;
    if (start > lastIndex) {
      tokens.push({ type: 'text', value: text.slice(lastIndex, start) });
    }
    tokens.push({ type: 'mention', value: match[0] });
    lastIndex = start + match[0].length;
  }

  if (lastIndex < text.length) {
    tokens.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return tokens;
}

/**
 * 将消息文本拆分为三类 token：
 * - `mention`：`@xxx[yyy]` 格式的资源提及（`buildMentionDisplay` 生成）
 * - `command`：`/xxx` 格式的功能指令
 * - `text`：普通文本
 */
export function splitTextWithMentionsAndCommands(
  text: string
): Array<{ type: 'mention' | 'command' | 'text'; value: string; resourceId?: string }> {
  if (!text) return [{ type: 'text', value: '' }];

  // 资源 mention 格式：@类型[名称]，如 @角色[张三-近景]，可选携带 <UUID> 后缀
  const resourceMentionRegex = new RegExp(`@[^\\s@/[]+\\[[^\\]]+\\](?:<(${UUID_PATTERN})>)?`, 'g');
  // 指令格式：/xxx（到空白/标点结束）
  const commandRegex = /\/[^\s，,。.？?！!：:；;\n]+/g;

  type RawToken = {
    type: 'mention' | 'command';
    start: number;
    end: number;
    value: string;
    resourceId?: string;
  };
  const rawTokens: RawToken[] = [];

  for (const match of text.matchAll(resourceMentionRegex)) {
    const resourceId = match[1];
    // value 始终为不含 UUID 的标签；end 覆盖整段（含 <UUID>）以吞掉后缀，避免残留为文本
    const value = stripMentionResourceIds(match[0]);
    rawTokens.push({
      type: 'mention',
      start: match.index ?? 0,
      end: (match.index ?? 0) + match[0].length,
      value,
      resourceId,
    });
  }
  for (const match of text.matchAll(commandRegex)) {
    rawTokens.push({
      type: 'command',
      start: match.index ?? 0,
      end: (match.index ?? 0) + match[0].length,
      value: match[0],
    });
  }

  // 按位置排序，去除重叠（优先资源 mention）
  rawTokens.sort((a, b) => a.start - b.start);
  const deduped: RawToken[] = [];
  let lastEnd = 0;
  for (const token of rawTokens) {
    if (token.start >= lastEnd) {
      deduped.push(token);
      lastEnd = token.end;
    }
  }

  const result: Array<{
    type: 'mention' | 'command' | 'text';
    value: string;
    resourceId?: string;
  }> = [];
  let cursor = 0;
  for (const token of deduped) {
    if (token.start > cursor) {
      result.push({ type: 'text', value: text.slice(cursor, token.start) });
    }
    result.push({
      type: token.type,
      value: token.value,
      ...(token.resourceId ? { resourceId: token.resourceId } : {}),
    });
    cursor = token.end;
  }
  if (cursor < text.length) {
    result.push({ type: 'text', value: text.slice(cursor) });
  }

  return result;
}

/** 带历史版本的资源类型（分镜图/视频/资产角色场景道具），用于统一追加 `V{seq}` */
const VERSIONED_MENTION_TYPES: ReadonlySet<MentionResource['type']> = new Set([
  'storyboard',
  'video',
  'character',
  'location',
  'prop',
]);

/**
 * 计算版本后缀 `V{seq}`。仅对带历史版本的类型且携带有效 versionSeq 时返回；
 * 上传参考（submitResourceType===4）不追加版本。
 * 用于插入输入框：`@分镜[方案名V1]`。
 */
export function buildMentionVersionSuffix(resource: MentionResource): string {
  if (resource.submitResourceType === 4) return '';
  if (!VERSIONED_MENTION_TYPES.has(resource.type)) return '';
  const seq = resource.versionSeq;
  return typeof seq === 'number' && seq > 0 ? `V${seq}` : '';
}

/**
 * 引用列表面板用的版本后缀：`（V{seq}）`（全角括号）。
 * 规则与 buildMentionVersionSuffix 一致；插入输入框仍用无括号的 V{seq}。
 */
export function buildMentionListVersionSuffix(resource: MentionResource): string {
  const suffix = buildMentionVersionSuffix(resource);
  return suffix ? `（${suffix}）` : '';
}

/** 引用列表卡片标题的基础名（不含版本）。 */
export function buildMentionListBaseName(resource: MentionResource): string {
  const shouldPreferNameWithTag =
    resource.type === 'character' || resource.type === 'location' || resource.type === 'prop';
  if (shouldPreferNameWithTag && resource.tagLabel) {
    return `${resource.elementName}-${resource.tagLabel}`;
  }
  return resource.elementName;
}

/**
 * 引用列表展示名：方案名/视频名/资产名 + `（V{seq}）`。
 * 例：`特写（V1）`、`开场运镜（V2）`、`张三-近景（V1）`。
 */
export function buildMentionListLabel(resource: MentionResource): string {
  return `${buildMentionListBaseName(resource)}${buildMentionListVersionSuffix(resource)}`;
}

export function buildMentionMark(resource: MentionResource): string {
  const baseName = resource.tagLabel
    ? `${resource.elementName}-${resource.tagLabel}`
    : resource.elementName;
  const displayName = `${baseName}${buildMentionVersionSuffix(resource)}`;
  const tagId = resource.tagId ?? '';
  return `@[${displayName}](${resource.type}:${resource.elementId}:${tagId}:${resource.resourceId})`;
}

export function buildMentionDisplay(resource: MentionResource): string {
  if (resource.isDigitalHuman) {
    return `@真人形象[${resource.elementName}]`;
  }

  const uploadReferenceLabelMap: Partial<Record<MentionResource['type'], string>> = {
    footage: '参考图',
    video: '参考视频',
    sound: '参考音频',
  };
  const typeLabelMap: Record<MentionResource['type'], string> = {
    storyboard: '分镜',
    video: '视频',
    sound: '音效',
    voice: '配音',
    character: '角色',
    location: '场景',
    prop: '道具',
    footage: '文件',
  };
  const typeLabel =
    resource.submitResourceType === 4 || resource.forceUploadReferenceTab
      ? (uploadReferenceLabelMap[resource.type] ??
        (resource.type === 'video'
          ? '参考视频'
          : resource.type === 'sound' || resource.type === 'voice'
            ? '参考音频'
            : '参考图'))
      : (typeLabelMap[resource.type] ?? '资源');
  const shouldPreferNameWithTag =
    !resource.forceUploadReferenceTab &&
    (resource.type === 'character' || resource.type === 'location' || resource.type === 'prop');

  const baseName =
    shouldPreferNameWithTag && resource.tagLabel
      ? `${resource.elementName}-${resource.tagLabel}`
      : resource.elementName;
  const displayName = `${baseName}${buildMentionVersionSuffix(resource)}`;

  return `@${typeLabel}[${displayName}]`;
}

export function mentionResourceHasSelectableAssets(resource: MentionResource): boolean {
  return Boolean(resource.resourceId && resource.resourceUrl);
}

function inferMediaKindFromUrl(url: string | undefined | null): 'image' | 'video' | 'audio' | null {
  if (!url) return null;
  const path = url.toLowerCase().split('?')[0]?.split('#')[0] ?? '';
  if (/\.(png|jpe?g|webp|gif|bmp|avif|heic)$/.test(path)) return 'image';
  if (/\.(mp4|mov|webm|m4v|avi|mkv)$/.test(path)) return 'video';
  if (/\.(mp3|wav|aac|ogg|m4a|flac)$/.test(path)) return 'audio';
  return null;
}

export function getMentionResourceMediaKind(
  resource: MentionResource
): 'image' | 'video' | 'audio' | 'unknown' {
  // 生成引用角色优先：D12 参考视频常带封面 image/* mime，不得被降成图片，
  // 否则 auto-inject 只看见 image → 切 reference_image，随后 draft role=reference_video
  // 再抢回 reference_video → imageMax=0 toast / 死循环。
  const role = resource.generationReferenceRole;
  if (role === 'reference_video' || role === 'video_extend_source') return 'video';
  if (role === 'reference_audio') return 'audio';
  if (role === 'reference_image' || role === 'first_frame' || role === 'last_frame') {
    return 'image';
  }

  // type=video 同样优先于封面 mime（连线入框时尚无 role 时）。
  if (resource.type === 'video') return 'video';
  if (resource.type === 'sound' || resource.type === 'voice') return 'audio';

  const mimeType = resource.mimeType?.toLowerCase();
  if (mimeType?.startsWith('video/')) return 'video';
  if (mimeType?.startsWith('audio/')) return 'audio';
  if (mimeType?.startsWith('image/')) return 'image';

  if (resource.type === 'storyboard') return 'image';
  if (resource.type === 'character' || resource.type === 'location' || resource.type === 'prop') {
    return 'image';
  }
  // 实拍：mime 已在上方优先；无 mime 时按 URL 推断，缺信号默认 image（与角色关联同类）。
  if (resource.type === 'footage') {
    return (
      inferMediaKindFromUrl(resource.resourceUrl) ??
      inferMediaKindFromUrl(resource.thumbnailUrl) ??
      'image'
    );
  }
  // mime/type 未知时，用 URL 扩展名兜底，避免明显图片源被判 unknown 导致新建菜单消失。
  return (
    inferMediaKindFromUrl(resource.resourceUrl) ??
    inferMediaKindFromUrl(resource.thumbnailUrl) ??
    'unknown'
  );
}

export function extractMentionDisplay(mark: string): string | null {
  const match = mark.match(/^@\[(.+?)\]\(/);
  return match?.[1] ?? null;
}
